<template>
  <div class="sims">
    <header class="sims-hero">
      <div>
        <div class="zh-eyebrow"><span class="zh-dot"></span> Sales simulator · administrators only</div>
        <h1 class="zh-title">Saved <span>simulations</span></h1>
        <p class="zh-sub">Every owner you have shown the numbers to, where the conversation is and what is at stake.</p>
      </div>
      <router-link to="/simulator" class="sa-btn primary"><i class="fas fa-plus"></i><span>New simulation</span></router-link>
    </header>

    <div class="pipeline" v-if="data">
      <button v-for="s in statuses" :key="s" type="button" class="pipe" :class="{ on: filter === s }" :style="{ '--c': colors[s] }" @click="filter = filter === s ? null : s">
        <span class="pipe-top"><i :class="icons[s]"></i> {{ labels[s] }}</span>
        <b>{{ data.pipeline[s].count }}</b>
        <small>{{ eurShort(data.pipeline[s].clientNet) }} · {{ tonsShort(data.pipeline[s].tons) }} CO₂</small>
      </button>
    </div>

    <div class="sims-bar" v-if="data && data.items.length">
      <div class="search"><i class="fas fa-search"></i><input v-model="q" type="search" placeholder="Search reference, owner, company, place…"></div>
      <span class="count">{{ shown.length }} of {{ data.items.length }}</span>
      <button v-if="filter" type="button" class="link" @click="filter = null">Show every status</button>
    </div>

    <div v-if="loading" class="sims-empty"><i class="fas fa-circle-notch fa-spin"></i> Loading…</div>
    <div v-else-if="error" class="sims-empty error"><i class="fas fa-exclamation-triangle"></i> {{ error }}</div>
    <div v-else-if="!data.items.length" class="sims-empty">
      <i class="fas fa-seedling"></i>
      <b>No simulations saved yet</b>
      <span>Make the first one with an owner and save it to find it here.</span>
      <router-link to="/simulator" class="sa-btn primary"><i class="fas fa-plus"></i><span>New simulation</span></router-link>
    </div>

    <div v-else class="cards">
      <article v-for="(s, i) in shown" :key="s.id" class="card-sim" :style="{ animationDelay: Math.min(i, 12) * 0.04 + 's', '--c': colors[s.status] }">
        <div class="cs-top">
          <router-link :to="`/simulator/${s.id}`" class="cs-ref">{{ s.reference }}</router-link>
          <b-dropdown size="sm" variant="link" toggle-class="cs-status" no-caret right>
            <template #button-content><span class="st" :style="{ '--c': colors[s.status] }"><i :class="icons[s.status]"></i> {{ labels[s.status] }} <i class="fas fa-caret-down"></i></span></template>
            <b-dropdown-item v-for="st in statuses" :key="st" :active="st === s.status" @click="setStatus(s, st)"><i :class="icons[st]" :style="{ color: colors[st] }"></i> {{ labels[st] }}</b-dropdown-item>
          </b-dropdown>
        </div>
        <div class="cs-who">
          <span v-if="s.contactName || s.company"><i class="fas fa-user"></i> {{ [s.contactName, s.company].filter(Boolean).join(' · ') }}</span>
          <span v-if="s.locationLabel"><i class="fas fa-map-marker-alt"></i> {{ s.locationLabel }}</span>
        </div>
        <div class="cs-figs">
          <div class="cs-money">{{ eur(s.summary ? s.summary.clientNet : 0) }}<small>in {{ s.summary ? s.summary.period : '—' }} years</small>
            <span class="cs-co2" v-if="s.summary"><i class="fas fa-cloud-download-alt"></i> {{ fmtT(tonsOf(s)) }} t CO₂<template v-if="s.summary.additionalTons > 0"> · {{ fmtT(s.summary.additionalTons) }} t additional</template></span>
          </div>
          <div class="cs-meta">
            <span><b>{{ fmtHa(s.areaHa) }}</b> ha</span>
            <span v-if="s.summary"><b>{{ eur(s.summary.perHaYear) }}</b> /ha/yr</span>
            <span v-if="s.summary && s.summary.practices && s.summary.practices.length"><i class="fas fa-plus-circle"></i> {{ s.summary.practices.map(practiceName).join(', ') }}</span>
          </div>
        </div>
        <div class="cs-contact" v-if="s.email || s.phone">
          <a v-if="s.email" :href="`mailto:${s.email}`"><i class="fas fa-envelope"></i> {{ s.email }}</a>
          <a v-if="s.phone" :href="`tel:${s.phone}`"><i class="fas fa-phone"></i> {{ s.phone }}</a>
        </div>
        <div class="cs-foot">
          <span class="fu" v-if="s.followUp" :class="{ due: isDue(s.followUp) }"><i class="fas fa-bell"></i> Follow up {{ niceDate(s.followUp) }}</span>
          <span class="upd">Updated {{ ago(s.updatedAt) }}{{ s.updatedBy ? ` by ${shortName(s.updatedBy)}` : '' }}</span>
          <span class="acts">
            <router-link :to="`/simulator/${s.id}`" title="Open"><i class="fas fa-external-link-alt"></i></router-link>
            <router-link :to="`/simulator?from=${s.id}`" title="Duplicate as a new simulation"><i class="far fa-copy"></i></router-link>
            <button type="button" title="Delete" @click="remove(s)"><i class="far fa-trash-alt"></i></button>
          </span>
        </div>
      </article>
      <div v-if="!shown.length" class="sims-empty small">Nothing matches.</div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import { mapGetters } from 'vuex';
import Simulator from '../api/Simulator';
import { STATUS_LABELS, STATUS_ICONS, STATUS_COLORS } from '../components/Simulator/statuses';
import adminOnlyPage from '../mixins/adminOnlyPage';

const PRACTICES = { fertilizing: 'fertilizing', thinning: 'thinning', finalCut: 'postponed cut' };

export default {
  name: 'Simulations',
  mixins: [adminOnlyPage],
  beforeRouteEnter(to, from, next) {
    next(async (vm) => {
      if (!vm.getWeb2ActiveAccount) await vm.$store.dispatch('user/initWeb2');
      if (!vm.getWeb2ActiveAccount || vm.getWeb2ActiveAccount.role.name !== 'Administrator') vm.$router.replace('/');
    });
  },
  data() {
    return { data: null, loading: true, error: null, q: '', filter: null, labels: STATUS_LABELS, icons: STATUS_ICONS, colors: STATUS_COLORS };
  },
  computed: {
    ...mapGetters('user', ['getWeb2ActiveAccount']),
    statuses() { return (this.data && this.data.statuses) || Object.keys(STATUS_LABELS); },
    shown() {
      const q = this.q.trim().toLowerCase();
      return this.data.items.filter((s) => (!this.filter || s.status === this.filter)
        && (!q || [s.reference, s.contactName, s.company, s.email, s.locationLabel, s.notes].some((v) => v && String(v).toLowerCase().includes(q))));
    },
  },
  created() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      try {
        this.data = (await Simulator.list()).data;
        this.error = null;
      } catch (e) {
        this.error = 'The simulations could not be loaded.';
      } finally {
        this.loading = false;
      }
    },
    eur(v) { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Number(v) || 0); },
    eurShort(v) {
      const n = Number(v) || 0;
      if (Math.abs(n) >= 1e6) return `€${(n / 1e6).toFixed(1)}M`;
      if (Math.abs(n) >= 1e4) return `€${Math.round(n / 1e3)}k`;
      return this.eur(n);
    },
    fmtT(v) { return new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(Number(v) || 0); },
    tonsShort(v) { const n = Number(v) || 0; return n >= 1e4 ? `${Math.round(n / 1e3)}k t` : `${this.fmtT(n)} t`; },
    tonsOf(s) { const m = s.summary || {}; return m.tons !== undefined ? Number(m.tons) || 0 : (Number(m.baseTons) || 0) + (Number(m.additionalTons) || 0); },
    fmtHa(v) { return new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 }).format(Number(v) || 0); },
    practiceName(k) { return PRACTICES[k] || k; },
    shortName(n) { return String(n).split('@')[0]; },
    niceDate(d) { return new Date(`${d}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); },
    isDue(d) { return d <= new Date().toISOString().slice(0, 10); },
    ago(ms) {
      const s = Math.max(0, (Date.now() - ms) / 1000);
      if (s < 60) return 'just now';
      if (s < 3600) return `${Math.round(s / 60)} min ago`;
      if (s < 86400) return `${Math.round(s / 3600)} h ago`;
      if (s < 86400 * 30) return `${Math.round(s / 86400)} days ago`;
      return new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    async setStatus(s, status) {
      if (s.status === status) return;
      const before = s.status;
      s.status = status;
      try {
        const updated = (await Simulator.setStatus(s.id, status)).data;
        Object.assign(s, { status: updated.status, updatedAt: updated.updatedAt, updatedBy: updated.updatedBy });
        this.recount();
      } catch (e) {
        s.status = before;
        this.$toasted.show('Could not change the status', { type: 'error', duration: 4000, theme: 'bubble', position: 'top-center' });
      }
    },
    recount() {
      const pipeline = Object.fromEntries(this.statuses.map((st) => [st, { count: 0, clientNet: 0, gross: 0, tons: 0 }]));
      for (const s of this.data.items) {
        const p = pipeline[s.status];
        if (!p) continue;
        p.count += 1;
        p.clientNet += (s.summary && Number(s.summary.clientNet)) || 0;
        p.gross += (s.summary && Number(s.summary.gross)) || 0;
        p.tons += this.tonsOf(s);
      }
      this.data.pipeline = pipeline;
    },
    async remove(s) {
      const answer = await Swal.fire({
        title: `Delete “${s.reference}”?`,
        text: 'The simulation and its contact details are deleted for good.',
        icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#e06a44',
      });
      if (!answer.isConfirmed) return;
      try {
        await Simulator.remove(s.id);
        this.data.items = this.data.items.filter((x) => x.id !== s.id);
        this.recount();
      } catch (e) {
        this.$toasted.show('Could not delete the simulation', { type: 'error', duration: 4000, theme: 'bubble', position: 'top-center' });
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '../components/Stats/intro.scss';

.sims { padding: 4px 0 40px; }

.sims-hero { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-bottom: 18px; }

.sa-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 16px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.86rem;
  text-decoration: none;
  &.primary { background: var(--accent-gradient); color: #fff; box-shadow: var(--shadow-glow); }
  &:hover { text-decoration: none; transform: translateY(-1px); color: #fff; }
}

.pipeline { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 10px; margin-bottom: 16px; }
.pipe {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 12px 14px;
  border-radius: 16px;
  border: 1px solid var(--border-subtle);
  border-top: 4px solid var(--c);
  background: var(--card-bg);
  box-shadow: var(--shadow-sm);
  text-align: left;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); }
  &.on { box-shadow: 0 0 0 2px var(--c), var(--shadow-md); }
  .pipe-top { font-size: 0.74rem; font-weight: 800; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.04em; i { color: var(--c); } }
  b { font-family: 'Sora', sans-serif; font-size: 1.6rem; color: var(--text-heading); line-height: 1.1; }
  small { font-size: 0.72rem; color: var(--text-muted); }
}

.sims-bar { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; flex-wrap: wrap; }
.search {
  flex: 1;
  min-width: 220px;
  max-width: 460px;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  background: var(--card-bg);
  i { color: var(--accent-green); }
  input { flex: 1; border: 0; outline: none; background: transparent; color: var(--text-main); }
}
.count { font-size: 0.8rem; color: var(--text-muted); }
.link { border: 0; background: none; padding: 0; color: var(--accent-green); font-weight: 700; font-size: 0.8rem; }

.cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(330px, 1fr)); gap: 14px; }
.card-sim {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border-radius: 20px;
  background: var(--card-bg);
  border: 1px solid var(--border-subtle);
  box-shadow: var(--shadow-md);
  position: relative;
  overflow: hidden;
  animation: cs-in 0.45s cubic-bezier(.2, .8, .2, 1) both;
  &::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 4px; background: var(--c); }
}
@keyframes cs-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }

.cs-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
.cs-ref { font-weight: 800; font-size: 1rem; color: var(--text-heading); line-height: 1.3; &:hover { color: var(--accent-green); text-decoration: none; } }
::v-deep .cs-status { padding: 0 !important; box-shadow: none !important; }
.st { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 999px; font-size: 0.72rem; font-weight: 800; color: var(--c); background: rgba(127, 127, 127, 0.1); background: color-mix(in srgb, var(--c) 14%, transparent); white-space: nowrap; }
.cs-who { display: flex; flex-direction: column; gap: 2px; font-size: 0.78rem; color: var(--text-muted); i { width: 14px; color: var(--accent-green); } }
.cs-figs { display: flex; align-items: flex-end; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
.cs-money { font-family: 'Sora', sans-serif; font-weight: 800; font-size: 1.5rem; color: var(--text-heading); line-height: 1; small { display: block; font-family: inherit; font-size: 0.7rem; font-weight: 600; color: var(--text-muted); margin-top: 4px; } }
.cs-co2 { display: block; margin-top: 6px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.76rem; font-weight: 700; color: var(--co2-accent, #159895); i { margin-right: 3px; } }
.cs-meta { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; font-size: 0.76rem; color: var(--text-muted); b { color: var(--text-heading); } i { color: var(--co2-accent, #159895); } }
.cs-contact { display: flex; flex-wrap: wrap; gap: 4px 14px; font-size: 0.78rem; a { color: var(--text-main); i { color: var(--accent-green); } } }
.cs-foot { display: flex; align-items: center; gap: 10px; padding-top: 10px; border-top: 1px solid var(--border-subtle); font-size: 0.72rem; color: var(--text-muted); flex-wrap: wrap; }
.fu { font-weight: 700; color: var(--text-main); &.due { color: #c4532f; } }
.upd { flex: 1; min-width: 120px; }
.acts { display: flex; gap: 4px; a, button { width: 30px; height: 30px; display: inline-flex; align-items: center; justify-content: center; border-radius: 9px; border: 0; background: var(--bg-primary); color: var(--text-muted); &:hover { color: var(--accent-green); } } button:hover { color: #c4532f; } }

.sims-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 60px 20px;
  border-radius: 20px;
  border: 1px dashed var(--border-color);
  color: var(--text-muted);
  text-align: center;
  i { font-size: 1.6rem; color: var(--accent-green); }
  b { color: var(--text-heading); }
  &.error i { color: #e06a44; }
  &.small { grid-column: 1 / -1; padding: 24px; }
}

@media (max-width: 1199px) { .pipeline { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 575px) {
  .pipeline { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .cards { grid-template-columns: minmax(0, 1fr); }
}
</style>
